import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import backend from "../../../api";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useUpload from "../../UserDashboard/Pages/Setup/Settings/Components/Profile/Shared/useUpload";

/* MUI */
import {
  Container,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Typography,
  IconButton,
  Stack,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const FALLBACK =
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1200&auto=format&fit=crop";

/* Advanced Quill setup */
const quillModules = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike"],
    ["blockquote", "code-block"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    [{ align: [] }, { direction: "rtl" }],
    ["link", "image"],
    ["clean"],
  ],
  clipboard: {
    // keep pasted markdown / formatting as close as possible
    matchVisual: false,
  },
};

const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "align",
  "direction",
  "link",
  "image",
];

export default function ContentManager() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const userId = user?._id;
  const role = user?.role;

  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [editingId, setEditingId] = useState(null);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("ritual");
  const [excerpt, setExcerpt] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");

  const { upload, loading: uploading } = useUpload();

  // Only allow admin here – redirect others
  useEffect(() => {
    if (role !== "admin") {
      navigate("/");
    }
  }, [role, navigate]);

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setCategory("ritual");
    setExcerpt("");
    setImageUrl("");
    setBodyHtml("");
  };

  const fetchContent = async () => {
    try {
      setLoadingList(true);
      const { data } = await backend.get("/api/content");
      setItems(data?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    try {
      const url = await upload(files);
      if (url) setImageUrl(url);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return;

    const payload = {
      title,
      category,
      excerpt,
      imageUrl: imageUrl || FALLBACK,
      bodyHtml,
    };

    try {
      setSaving(true);
      if (editingId) {
        await backend.put(`/api/content/${userId}/${editingId}`, payload);
      } else {
        await backend.post(`/api/content/${userId}`, payload);
      }
      await fetchContent();
      resetForm();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setTitle(item.title);
    setCategory(item.category || "ritual");
    setExcerpt(item.excerpt);
    setImageUrl(item.imageUrl || "");
    setBodyHtml(item.bodyHtml || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!userId) return;
    const ok = window.confirm("Are you sure you want to delete this guide?");
    if (!ok) return;

    try {
      setDeletingId(id);
      await backend.delete(`/api/content/${userId}/${id}`);
      setItems((prev) => prev.filter((g) => g._id !== id));
      if (editingId === id) {
        resetForm();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [items]
  );

  const textStats = useMemo(() => {
    if (!bodyHtml) return { words: 0, chars: 0 };
    const stripped = bodyHtml.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (!stripped) return { words: 0, chars: 0 };
    const words = stripped.split(" ").length;
    const chars = stripped.length;
    return { words, chars };
  }, [bodyHtml]);

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #F6F7FB 0%, #EFF2F7 60%, #F6F7FB 100%)",
        minHeight: "100vh",
        py: { xs: 2, md: 3.5 },
      }}
    >
      <Container maxWidth="lg">
        {/* Heading / Intro */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1.5}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "rgba(252,170,48,0.14)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DescriptionOutlinedIcon sx={{ color: "#FCAA30" }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "#37474F",
                  fontSize: { xs: "1.65rem", md: "1.9rem" },
                }}
              >
                Content Manager
              </Typography>
              <Typography variant="body2" sx={{ color: "#606060" }}>
                Create, design, and manage all Tawaf guides in a single place.
              </Typography>
            </Box>
          </Stack>
          <Chip
            label="Admin workspace"
            size="small"
            sx={{
              borderRadius: 999,
              bgcolor: "#fff",
              border: "1px solid #FFECB3",
              fontWeight: 600,
              color: "#8D6E63",
            }}
          />
        </Stack>

        <Grid container spacing={3}>
          {/* Left: Editor form (bigger, pro-looking) */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 14px 40px rgba(0,0,0,.10)",
                border: "1px solid #ECEFF1",
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 2, md: 3 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {editingId ? "Edit guide" : "Create new guide"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9E9E9E" }}>
                      Use the editor like a document tool: headings, lists, links,
                      code blocks, and more.
                    </Typography>
                  </Box>
                  {editingId && (
                    <Chip
                      label="Editing existing guide"
                      size="small"
                      sx={{
                        borderRadius: 999,
                        bgcolor: "#FFF3E0",
                        color: "#E65100",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Stack>

                <Divider sx={{ mb: 1.5 }} />

                <form onSubmit={handleSubmit}>
                  <Stack spacing={2.3}>
                    <TextField
                      label="Title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      fullWidth
                      required
                      placeholder="E.g. Complete Umrah Guide – From Home to Home"
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel id="category-label">Category</InputLabel>
                        <Select
                          labelId="category-label"
                          label="Category"
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                        >
                          <MenuItem value="ritual">Ritual guidance</MenuItem>
                          <MenuItem value="pretravel">Pre-travel guide</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label="Short excerpt"
                        value={excerpt}
                        onChange={(e) => setExcerpt(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        inputProps={{ maxLength: 260 }}
                        helperText={`${excerpt.length}/260`}
                        required
                        placeholder="A short summary that appears on cards and SEO snippets."
                      />
                    </Stack>

                    {/* Cover image upload */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "#37474F" }}
                      >
                        Cover image
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<AddPhotoAlternateIcon />}
                          disabled={uploading}
                          sx={{
                            textTransform: "none",
                            borderRadius: 999,
                            borderColor: "#FFCC80",
                          }}
                        >
                          {uploading ? "Uploading..." : "Upload image"}
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleImageUpload}
                          />
                        </Button>

                        {imageUrl && (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt="Cover"
                            sx={{
                              width: 110,
                              height: 76,
                              objectFit: "cover",
                              borderRadius: 2,
                              border: "1px solid #eee",
                            }}
                            onError={(e) => (e.currentTarget.src = FALLBACK)}
                          />
                        )}
                        {!imageUrl && (
                          <Typography variant="caption" sx={{ color: "#9E9E9E" }}>
                            If you don’t upload, a default image will be used.
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    {/* Rich editor */}
                    <Box>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 0.5 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: "#37474F" }}
                        >
                          Content
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#9E9E9E" }}>
                          {textStats.words} words · {textStats.chars} characters
                        </Typography>
                      </Stack>

                      <Box
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "1px solid #E0E0E0",
                          background: "#fff",
                          "& .ql-toolbar": {
                            borderColor: "#E0E0E0",
                            backgroundColor: "#FAFAFA",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          },
                          "& .ql-container": {
                            borderColor: "#E0E0E0",
                            minHeight: { xs: 280, md: 460 },
                            maxHeight: { xs: 380, md: 600 },
                            fontFamily:
                              "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                            fontSize: 15,
                          },
                          "& .ql-editor": {
                            lineHeight: 1.7,
                            padding: "14px 16px 32px",
                          },
                        }}
                      >
                        <ReactQuill
                          theme="snow"
                          value={bodyHtml}
                          onChange={setBodyHtml}
                          modules={quillModules}
                          formats={quillFormats}
                        />
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      flexWrap="wrap"
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={saving || !title.trim() || !excerpt.trim()}
                        sx={{
                          borderRadius: 999,
                          px: 3,
                          textTransform: "none",
                          fontWeight: 700,
                          background: "#FCAA30",
                          "&:hover": { background: "#ffbb55" },
                        }}
                      >
                        {saving ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                          >
                            <CircularProgress size={18} sx={{ color: "#fff" }} />
                            <span>Saving...</span>
                          </Stack>
                        ) : editingId ? (
                          "Update guide"
                        ) : (
                          "Publish guide"
                        )}
                      </Button>

                      {editingId && (
                        <Button
                          type="button"
                          variant="text"
                          onClick={resetForm}
                          sx={{ textTransform: "none" }}
                        >
                          Cancel edit
                        </Button>
                      )}

                      <Tooltip title="Preview how users will see it on guides page.">
                        <Button
                          type="button"
                          variant="outlined"
                          sx={{
                            textTransform: "none",
                            borderRadius: 999,
                            borderColor: "#CFD8DC",
                          }}
                          onClick={() => navigate("/guides")}
                        >
                          Open guides page
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </form>
              </CardContent>
            </Card>
          </Grid>

          {/* Right: Existing guides list */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 10px 26px rgba(0,0,0,.08)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                border: "1px solid #ECEFF1",
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 2, md: 3 },
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Existing guides
                  </Typography>
                  {loadingList && <CircularProgress size={20} />}
                </Stack>
                <Typography variant="caption" sx={{ color: "#9E9E9E", mb: 1 }}>
                  Click a guide to quickly load it into the editor for updates or
                  delete it from here.
                </Typography>

                {sortedItems.length === 0 && !loadingList && (
                  <Typography variant="body2" color="text.secondary">
                    No guides yet. Create your first guide on the left.
                  </Typography>
                )}

                <Stack spacing={1.5} sx={{ overflowY: "auto", maxHeight: 520 }}>
                  {sortedItems.map((item) => (
                    <Card
                      key={item._id}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        cursor: "pointer",
                        "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,.08)" },
                      }}
                      onClick={() => handleEdit(item)}
                    >
                      <Box sx={{ display: "flex" }}>
                        <Box
                          component="img"
                          src={item.imageUrl || FALLBACK}
                          alt={item.title}
                          sx={{
                            width: 90,
                            height: 70,
                            objectFit: "cover",
                          }}
                          onError={(e) => (e.currentTarget.src = FALLBACK)}
                        />
                        <Box
                          sx={{
                            flex: 1,
                            p: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, pr: 1 }}
                              noWrap
                            >
                              {item.title}
                            </Typography>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Edit guide">
                                <IconButton
                                  size="small"
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    handleEdit(item);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete guide">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={deletingId === item._id}
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      handleDelete(item._id);
                                    }}
                                  >
                                    {deletingId === item._id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <DeleteOutlineIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </Stack>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {item.excerpt}
                          </Typography>

                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mt: 0.5 }}
                          >
                            <Chip
                              size="small"
                              label={
                                item.category === "ritual"
                                  ? "Ritual"
                                  : item.category === "pretravel"
                                  ? "Pre-travel"
                                  : "Other"
                              }
                              sx={{ fontSize: 11 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(
                                item.updatedAt || item.createdAt
                              ).toLocaleDateString()}
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
